/**
 * Prague's SOKAK LAMBASI — the old gas street lamp — in the shared 120×120 LINE
 * asset box.
 *
 * The Home tab, all four passcode dots and the chat wallpaper draw this lamp.
 *
 * WHY THIS REPLACED A CATHEDRAL
 *
 * The first pass drew a gothic spire with a cross on it, and the owner's call was
 * that a cross-topped tower reads as a religious emblem rather than as a city —
 * on a chat theme it is a flag, and a flag is not something a person chooses a
 * wallpaper for. That is right, and it is worth stating plainly rather than
 * softening: the drawing was well made and the SUBJECT was wrong.
 *
 * The lamp is a better answer than a neutral substitute would have been, because
 * it is what the theme is actually about. Prague's icon is a city lit gold against
 * a blue night; the cathedral was one lit thing among many, but the lamp is the
 * thing that DOES the lighting. Prague still lights around a thousand of these by
 * hand, which is exactly the "Gothic, Historic, Timeless" the theme's own three
 * tags claim.
 *
 * WHAT IS LOAD-BEARING
 *
 *  - THE LANTERN IS A TAPERED BOX, wider at its foot than its shoulder. Drawn as a
 *    rectangle it is a box on a stick; the taper is what makes it a lamp.
 *  - THE LADDER BAR. The short crossbar under the lantern is where the lamplighter
 *    rests the ladder, and it is the one detail that says GAS rather than electric.
 *    It costs four points and it is the whole period.
 *  - THE POST IS THIN RELATIVE TO THE LANTERN — ten units against the lantern's
 *    thirty-five. A post that approaches the lantern's width makes a bollard; one
 *    drawn at a quarter of it makes a lollipop, which is what this was.
 *  - IT GLOWS. The lit state fills the panes and throws a halo, and that is the
 *    entire ON/OFF story of this theme: the lamps coming on over the river.
 *
 * Every subpath is wound clockwise so a nonzero fill unions them
 * ([[line-shared-glyph-modules]]).
 */

const f = ( v: number ) => +v.toFixed( 1 );

/** A polygon in clockwise order (y down), whichever order it arrives in. */
function cw( pts: [ number, number ][] ): string {
  let area = 0;
  for ( let i = 0; i < pts.length; i++ ) {
    const a = pts[ i ], b = pts[ ( i + 1 ) % pts.length ];
    area += a[ 0 ] * b[ 1 ] - b[ 0 ] * a[ 1 ];
  }
  const p = area < 0 ? pts.slice().reverse() : pts;
  return 'M' + p.map( ( q ) => `${ f( q[ 0 ] ) },${ f( q[ 1 ] ) }` ).join( ' L' ) + ' Z';
}

/** The same polygon wound the OTHER way, so a nonzero fill subtracts it from the
 *  shape it sits inside instead of adding to it. Used for the wallpaper's lamps,
 *  whose panes are holes rather than glass. */
function ccw( pts: [ number, number ][] ): string {
  const d = cw( pts.slice().reverse() );
  // cw() re-sorts by signed area, so reversing the input is not enough on its own:
  // rebuild the point list in the order cw() produced and flip it.
  const nums = d.slice( 1, -2 ).split( ' L' ).map( ( q ) => q.split( ',' ).map( Number ) );
  const rev = nums.slice().reverse();
  return 'M' + rev.map( ( q ) => `${ q[ 0 ] },${ q[ 1 ] }` ).join( ' L' ) + ' Z';
}

/* ---------------------------------------------------------------- geometry -- */

const CX = 60;
/** The lantern: its shoulder, its foot, and its two half-widths. */
/**
 * TALLER and SLIMMER than the first pass, which drew it 28 across by 32 down —
 * nearly square, which is a lantern on a garden path rather than a street lamp.
 * The real ones are about half again as tall as they are wide.
 *
 * STOCKIER than the second pass (owner 2026-08-15: "çok ince uzun"). That pass
 * corrected the squat lantern and overshot the whole object: at 30 units across
 * by 106 down the lamp was 1:3.5, which at the 22px a tab glyph actually renders
 * is a hairline with a bead on it. Every width here is up by about two fifths and
 * the height is down by a fifteenth, which puts it at 40 by 102 — 1:2.5, and
 * still plainly taller than it is wide, which a street lamp has to be.
 */
const LANT_TOP = 24, LANT_BOT = 60, LANT_HALF_TOP = 13.5, LANT_HALF_BOT = 17.5;
/** The post. */
const POST_TOP = 60, FOOT = 102, POST_HALF = 5;

/**
 * The IRONWORK: everything that is not glass and not glowing — the crown, the
 * post, the ladder bar and the base.
 *
 * One export rather than four, because at 22px they are one silhouette and
 * splitting them would only invite a caller to tint them apart.
 */
export const LAMP_IRON = [
  // the finial: a small ball on a stem
  cw( [ [ CX - 1.8, 14 ], [ CX - 1.8, 9 ], [ CX + 1.8, 9 ], [ CX + 1.8, 14 ] ] ),
  `M${ CX - 3.8 },9 a3.8,3.8 0 1,0 7.6,0 a3.8,3.8 0 1,0 -7.6,0 Z`,
  // the crown: a shallow cap, wider than the lantern's shoulder
  cw( [ [ CX - 20, LANT_TOP ], [ CX - 10, 13 ], [ CX + 10, 13 ], [ CX + 20, LANT_TOP ] ] ),
  // the collar under the crown
  cw( [ [ CX - 20, LANT_TOP ], [ CX - 20, LANT_TOP - 3.2 ],
        [ CX + 20, LANT_TOP - 3.2 ], [ CX + 20, LANT_TOP ] ] ),
  // the lantern's frame: the two uprights and the sill
  cw( [ [ CX - LANT_HALF_BOT, LANT_BOT ], [ CX - LANT_HALF_TOP, LANT_TOP ],
        [ CX - LANT_HALF_TOP + 4.2, LANT_TOP ], [ CX - LANT_HALF_BOT + 4.2, LANT_BOT ] ] ),
  cw( [ [ CX + LANT_HALF_BOT, LANT_BOT ], [ CX + LANT_HALF_TOP, LANT_TOP ],
        [ CX + LANT_HALF_TOP - 4.2, LANT_TOP ], [ CX + LANT_HALF_BOT - 4.2, LANT_BOT ] ] ),
  cw( [ [ CX - LANT_HALF_BOT - 3, LANT_BOT + 4.6 ], [ CX - LANT_HALF_BOT - 3, LANT_BOT ],
        [ CX + LANT_HALF_BOT + 3, LANT_BOT ], [ CX + LANT_HALF_BOT + 3, LANT_BOT + 4.6 ] ] ),
  // the post
  cw( [ [ CX - POST_HALF, FOOT ], [ CX - POST_HALF, POST_TOP + 4 ],
        [ CX + POST_HALF, POST_TOP + 4 ], [ CX + POST_HALF, FOOT ] ] ),
  // THE LADDER BAR — the one detail that says gas rather than electric.
  //
  // Its height took two goes in opposite directions. Eight units below the sill it
  // floated on the post as a random cross-piece; pushed right up under the sill it
  // merged with it and the two read as one thick double band. Twelve units down
  // leaves clear post above and below, which is where the real ones are.
  cw( [ [ CX - 16, 76 ], [ CX - 16, 72 ], [ CX + 16, 72 ], [ CX + 16, 76 ] ] ),
  // the base: two steps, so the lamp stands on the pavement rather than stopping
  cw( [ [ CX - 9, FOOT ], [ CX - 9, FOOT - 5.5 ], [ CX + 9, FOOT - 5.5 ], [ CX + 9, FOOT ] ] ),
  cw( [ [ CX - 13.5, FOOT + 5.5 ], [ CX - 13.5, FOOT ], [ CX + 13.5, FOOT ], [ CX + 13.5, FOOT + 5.5 ] ] ),
].join( ' ' );

/**
 * The GLASS: the four panes inside the lantern frame.
 *
 * Two columns by two rows, with the mullion between them left as iron. They are
 * exported separately from the frame so the lit state can fill them with light
 * while the unlit state simply leaves them the body colour — which is the whole
 * ON/OFF story of this theme.
 */
function panes( wind: ( p: [ number, number ][] ) => string ): string {
  const out: string[] = [];
  const mid = ( LANT_TOP + LANT_BOT ) / 2;
  // The bar UNDER the lower panes is 4.5 units, not 2 (owner 2026-08-15). It is
  // the lantern's sill and it carries the whole object's weight at small size: at
  // 2 units the wallpaper's punched panes ran almost to the bottom edge and the
  // lantern read as open at the foot, like a birdcage rather than a lamp.
  for ( const [ y0, y1 ] of [ [ LANT_TOP + 2, mid - 1.4 ], [ mid + 1.4, LANT_BOT - 4.5 ] ] ) {
    // the frame narrows with height, so each pane's width follows its own row
    const w0 = LANT_HALF_TOP + ( LANT_HALF_BOT - LANT_HALF_TOP )
      * ( ( y0 - LANT_TOP ) / ( LANT_BOT - LANT_TOP ) ) - 4.6;
    const w1 = LANT_HALF_TOP + ( LANT_HALF_BOT - LANT_HALF_TOP )
      * ( ( y1 - LANT_TOP ) / ( LANT_BOT - LANT_TOP ) ) - 4.6;
    for ( const side of [ -1, 1 ] ) {
      out.push( wind( [
        [ CX + side * 2, y0 ], [ CX + side * w0, y0 ],
        [ CX + side * w1, y1 ], [ CX + side * 2, y1 ],
      ] ) );
    }
  }
  return out.join( ' ' );
}

export const LAMP_GLASS = panes( cw );

/**
 * Where the halo goes and how big it is. The SHAPE is not exported, only these
 * three numbers, because a halo must be drawn as a RADIAL GRADIENT by the caller
 * and never as a flat fill.
 *
 * The first pass exported an ellipse and every caller filled it: at 18% opacity on
 * a night ground it came back as a hard-edged olive DISC parked behind the lamp,
 * which is the one thing a glow must not have — an edge.
 */
export const LAMP_GLOW_CX = CX;
export const LAMP_GLOW_CY = ( LANT_TOP + LANT_BOT ) / 2;
export const LAMP_GLOW_R = 42;

/** The whole lamp as ONE path — iron and glass together. This is what the idle
 *  tab and the unentered passcode dot draw: an unlit lamp is one silhouette, and
 *  its panes are simply part of it. */
export const LAMP = `${ LAMP_IRON } ${ LAMP_GLASS }`;

/**
 * A SIMPLER lamp for the wallpaper's smallest grains: the lantern and a short
 * stub of post, no ladder bar and no base.
 *
 * Same reason Tallinn's simple flake exists. At a third of this box the ladder bar
 * is a quarter of a pixel and the base is two, so both come back as lint on the
 * post; the lantern is the part that reads.
 */
export const LAMP_SIMPLE = [
  cw( [ [ CX - 1.8, 14 ], [ CX - 1.8, 9 ], [ CX + 1.8, 9 ], [ CX + 1.8, 14 ] ] ),
  `M${ CX - 3.8 },9 a3.8,3.8 0 1,0 7.6,0 a3.8,3.8 0 1,0 -7.6,0 Z`,
  cw( [ [ CX - 20, LANT_TOP ], [ CX - 13, 14 ], [ CX + 13, 14 ], [ CX + 20, LANT_TOP ] ] ),
  cw( [ [ CX - LANT_HALF_BOT, LANT_BOT ], [ CX - LANT_HALF_TOP, LANT_TOP ],
        [ CX + LANT_HALF_TOP, LANT_TOP ], [ CX + LANT_HALF_BOT, LANT_BOT ] ] ),
  // A LONG post, running most of the way to the foot. It was a short stub, which
  // was fine while the lantern was slim: once the lantern went stocky the stub
  // gave the silhouette a head-and-body proportion and the wallpaper filled up
  // with little FIGURES rather than lamps (owner 2026-08-15: the chat background
  // looks broken). What makes this read as a lamp at a third of its box is the
  // ratio of a wide lantern to a long thin stem, not the lantern alone.
  cw( [ [ CX - POST_HALF * 0.8, 100 ], [ CX - POST_HALF * 0.8, LANT_BOT ],
        [ CX + POST_HALF * 0.8, LANT_BOT ], [ CX + POST_HALF * 0.8, 100 ] ] ),
  // THE PANES AS HOLES (owner 2026-08-15). The wallpaper draws this lamp in ONE
  // flat tint, so a solid lantern is a solid blob — the thing that makes it a
  // lantern at a third of its box is the light coming THROUGH it. Wound
  // counter-clockwise so the nonzero fill subtracts them, which lets the chat
  // skin behind the transparent wallpaper show through the glass.
  panes( ccw ),
].join( ' ' );

/** A MOTE — one speck of light drifting off a lamp, the wallpaper's second
 *  element. A soft four-pointed spark rather than a disc: a round mote is a
 *  bubble, and this theme already has a river full of those. */
export const LAMP_MOTE = cw( [
  [ 60, 46 ], [ 63.5, 56.5 ], [ 74, 60 ], [ 63.5, 63.5 ], [ 60, 74 ],
  [ 56.5, 63.5 ], [ 46, 60 ], [ 56.5, 56.5 ],
] );

/* ------------------------------------------------------- passcode colours --
 * Held here rather than in the passcode component: line-lock (the preview the
 * marketplace screenshot shows) and line-page (the exported PNGs) both read
 * them, and every time this family kept two copies of a colour they drifted.
 */

/**
 * The four entered lamps, on the NIGHT upper zone this skin's passcode screen
 * paints (#0A1826).
 *
 * They LIGHT UP: cold iron, then the first blue of a mantle catching, then
 * lamplight, then the full warm gold. A passcode filling up is the lamplighter
 * going down the street, which is a thing that still happens in this city every
 * evening and is the nicest ramp in the whole run.
 *
 * Measured on that zone: 3.42 / 4.79 / 6.66 / 9.02 to 1.
 */
export const LAMP_PC_COLORS = [ '#5F7C9C', '#8A8CA0', '#C08F45', '#E3CA77' ];

/**
 * ONE tone for every unentered lamp — the frame never changes per digit (the rule
 * Aurora's orbs settled).
 *
 * 1.72:1 on the night zone: present, and plainly a lamp nobody has lit yet.
 */
export const LAMP_PC_OFF = '#2C4360';

/**
 * A per-digit lean, alternating side, about the FOOT.
 *
 * The smallest set in the run alongside Prague's old cathedral, and for the same
 * reason: a lamp post is a plumb-line object. A leaning street lamp is one a car
 * has hit.
 */
export const LAMP_PC_TILTS = [ -4, 3, -2.5, 5 ];
